import { App as AntApp, ConfigProvider, theme } from "antd";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { useThemeStore } from "../../stores/themeStore";
import { getTransportPalette } from "../../theme/transportThemes";

type AppProvidersProps = {
  children: ReactNode;
};

export function AppProviders({ children }: AppProvidersProps) {
  const mode = useThemeStore((x) => x.mode);
  const uiVariant = useThemeStore((x) => x.uiVariant);
  const accent = useThemeStore((x) => x.accent);
  const palette = getTransportPalette(accent, mode);

  const isHV = uiVariant === "highVisibility";

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--app-bg", palette.appBackground);
    root.style.setProperty("--panel-bg", palette.panelBackground);
    root.style.setProperty("--elevated-bg", palette.elevatedBackground);
    root.style.setProperty("--primary", palette.primary);
    root.dataset.themeMode = mode;
  }, [mode, palette]);

  const themeConfig = {
    algorithm: mode === "dark" ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: palette.primary,
      colorInfo: palette.info,
      colorSuccess: palette.success,
      colorWarning: palette.warning,
      colorError: palette.error,
      colorBgBase: palette.appBackground,
      colorBgLayout: palette.appBackground,
      colorBgContainer: palette.panelBackground,
      colorBgElevated: palette.elevatedBackground,
      borderRadius: 8,
      ...(isHV && {
        fontSize: 15,
        colorText: mode === "dark" ? "#f5f5f5" : "#111111",
        colorTextSecondary: mode === "dark" ? "#d9d9d9" : "#222222",
        colorTextTertiary: mode === "dark" ? "#bfbfbf" : "#444444",
        colorTextQuaternary: mode === "dark" ? "#a0a0a0" : "#555555",
        colorBorder: mode === "dark" ? "#666666" : "#555555",
        controlHeight: 40,
        controlHeightSM: 32,
        controlHeightLG: 48,
      })
    },
    components: {
      Layout: {
        bodyBg: palette.appBackground,
        headerBg: palette.headerBackground,
        siderBg: palette.siderBackground,
        triggerBg: palette.siderBackground,
        triggerColor: palette.siderText
      },
      Menu: {
        itemSelectedBg: mode === "dark" ? "rgba(255,255,255,0.16)" : "rgba(13,110,253,0.12)",
        itemSelectedColor: palette.primary
      },
      Modal: {
        contentBg: palette.elevatedBackground,
        headerBg: palette.elevatedBackground
      },
      Drawer: {
        colorBgElevated: palette.elevatedBackground
      }
    }
  };

  return (
    <ConfigProvider theme={themeConfig}>
      <AntApp>{children}</AntApp>
    </ConfigProvider>
  );
}
